"use client";

import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { updateUserSearches } from "@lib/util/update-user-searches";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useCustomer } from "../shared/customer-context";
import logger from "@lib/util/shared/logger";
const HotelsSearchContext = /*#__PURE__*/createContext(null);
const defaultHotelsSearch = {
  occupations: [{
    adults: 1,
    children: 0,
    children_ages: []
  }],
  destination: {
    id: "",
    value: ""
  },
  dates: {
    check_in: new Date(),
    check_out: new Date(Date.now() + 24 * 60 * 60 * 1000) // Tomorrow
  }
};
export const HotelsSearchProvider = _ref => {
  let {
    children,
    router
  } = _ref;
  const {
    0: searchState,
    1: setSearchState
  } = useState();
  const {
    0: searchQueryId,
    1: setSearchQueryId
  } = useState();
  const {
    0: destinationSelection,
    1: setDestinationSelection
  } = useState();
  const {
    getLoggedCustomer
  } = useCustomer();
  const APP = "hotels";

  // Ref to track the previous search for comparison
  const previousSearchRef = useRef();

  // Initialize from URL parameters
  useEffect(() => {
    const routeSearch = router.getQueryParam('search');
    const optionId = router.getQueryParam('optionId');
    console.log('🔍 HotelsSearchProvider: Initializing from URL', {
      routeSearch,
      optionId
    });
    if (optionId) return;
    if (routeSearch) {
      try {
        var _parsedSearch$destina;
        const parsedSearch = JSON.parse(routeSearch);
        console.log('🔍 HotelsSearchProvider: Parsed search from URL', parsedSearch);
        setSearchState(parsedSearch);

        // Initialize destinationSelection from URL
        if ((_parsedSearch$destina = parsedSearch.destination) !== null && _parsedSearch$destina !== void 0 && _parsedSearch$destina.id) {
          const destinationOption = {
            id: parsedSearch.destination.id,
            title: parsedSearch.destination.value,
            value: parsedSearch.destination
          };
          console.log('🔍 HotelsSearchProvider: Setting destinationSelection from URL', destinationOption);
          setDestinationSelection(destinationOption);
        }
      } catch (error) {
        console.error('Error parsing search from URL:', error);
      }
    }
  }, [router]);

  // Listen to URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const routeSearch = router.getQueryParam('search');
      console.log('🔍 HotelsSearchProvider: URL changed, new search param:', routeSearch);
      if (routeSearch) {
        try {
          var _parsedSearch$destina2;
          const parsedSearch = JSON.parse(routeSearch);
          console.log('🔍 HotelsSearchProvider: Updating search state from URL change', parsedSearch);
          setSearchState(parsedSearch);
          if ((_parsedSearch$destina2 = parsedSearch.destination) !== null && _parsedSearch$destina2 !== void 0 && _parsedSearch$destina2.id) {
            const destinationOption = {
              id: parsedSearch.destination.id,
              title: parsedSearch.destination.value,
              value: parsedSearch.destination
            };
            setDestinationSelection(destinationOption);
          }
        } catch (error) {
          console.error('Error parsing search from URL change:', error);
        }
      }
    };

    // Listen to popstate events (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [router]);

  // Function to check if search is valid and not empty
  const isValidSearch = search => {
    var _search$destination, _search$destination2, _search$dates, _search$dates2;
    return !!(search && (_search$destination = search.destination) !== null && _search$destination !== void 0 && _search$destination.id && (_search$destination2 = search.destination) !== null && _search$destination2 !== void 0 && _search$destination2.value && (_search$dates = search.dates) !== null && _search$dates !== void 0 && _search$dates.check_in && (_search$dates2 = search.dates) !== null && _search$dates2 !== void 0 && _search$dates2.check_out && search.occupations && search.occupations.length > 0 && search.occupations.some(occ => occ.adults > 0));
  };

  // Function to check if two searches are different
  const isSearchDifferent = (currentSearch, previousSearch) => {
    if (!previousSearch) return true;

    // Compare essential search parameters
    const currentKey = JSON.stringify({
      destination: currentSearch.destination,
      dates: currentSearch.dates,
      occupations: currentSearch.occupations
    });
    const previousKey = JSON.stringify({
      destination: previousSearch.destination,
      dates: previousSearch.dates,
      occupations: previousSearch.occupations
    });
    return currentKey !== previousKey;
  };
  useEffect(() => {
    if (searchState && isValidSearch(searchState)) {
      // Only add to history if search is different from previous
      if (isSearchDifferent(searchState, previousSearchRef.current)) {
        var _getLoggedCustomer$id;
        logger.log('📝 Adding new hotel search to history:', {
          destination: searchState.destination.value,
          check_in: searchState.dates.check_in,
          check_out: searchState.dates.check_out,
          adults: searchState.occupations.reduce((sum, occ) => sum + occ.adults, 0)
        });
        updateUserSearches((_getLoggedCustomer$id = getLoggedCustomer().id) !== null && _getLoggedCustomer$id !== void 0 ? _getLoggedCustomer$id : 0, 'hotels', searchState);

        // Update the previous search reference
        previousSearchRef.current = searchState;
      } else {
        logger.log('📝 Hotel search is the same as previous, skipping history update');
      }
    } else if (searchState) {
      var _searchState$destinat, _searchState$dates, _searchState$dates2, _searchState$occupati, _searchState$occupati2;
      logger.log('📝 Hotel search is not valid, skipping history update:', {
        hasDestination: !!((_searchState$destinat = searchState.destination) !== null && _searchState$destinat !== void 0 && _searchState$destinat.id),
        hasDates: !!((_searchState$dates = searchState.dates) !== null && _searchState$dates !== void 0 && _searchState$dates.check_in) && !!((_searchState$dates2 = searchState.dates) !== null && _searchState$dates2 !== void 0 && _searchState$dates2.check_out),
        hasOccupations: !!((_searchState$occupati = searchState.occupations) !== null && _searchState$occupati !== void 0 && _searchState$occupati.length),
        adults: (_searchState$occupati2 = searchState.occupations) === null || _searchState$occupati2 === void 0 ? void 0 : _searchState$occupati2.reduce((sum, occ) => sum + occ.adults, 0)
      });
    }
  }, [searchState, getLoggedCustomer]);
  const resetSearchQueryId = () => {
    setSearchQueryId(undefined);
  };
  const updateDestination = destination => {
    setSearchState(prevState => {
      if (!prevState) return prevState;
      return _objectSpread(_objectSpread({}, prevState), {}, {
        destination
      });
    });
  };
  const updateDates = dates => {
    setSearchState(prevState => {
      if (!prevState) return prevState;
      return _objectSpread(_objectSpread({}, prevState), {}, {
        dates
      });
    });
  };
  const updateOccupations = occupations => {
    setSearchState(prevState => {
      if (!prevState) return prevState;
      return _objectSpread(_objectSpread({}, prevState), {}, {
        occupations
      });
    });
  };
  const clearDestination = () => {
    setSearchState(prevState => {
      if (!prevState) return prevState;
      return _objectSpread(_objectSpread({}, prevState), {}, {
        destination: {
          id: "",
          value: ""
        }
      });
    });
    setDestinationSelection(undefined);
  };
  const resetSearch = () => {
    setSearchState(defaultHotelsSearch);
    setDestinationSelection(undefined);
    setSearchQueryId(undefined);
  };

  // Initialize destinationSelection when search changes
  useEffect(() => {
    var _searchState$destinat2;
    if (searchState !== null && searchState !== void 0 && (_searchState$destinat2 = searchState.destination) !== null && _searchState$destinat2 !== void 0 && _searchState$destinat2.id && !destinationSelection) {
      const destinationOption = {
        id: searchState.destination.id,
        title: searchState.destination.value,
        value: searchState.destination
      };
      setDestinationSelection(destinationOption);
    }
  }, [searchState]); // Removed destinationSelection from dependencies to prevent conflicts

  return __jsx(HotelsSearchContext.Provider, {
    value: {
      search: searchState,
      setSearch: setSearchState,
      searchQueryId,
      setSearchQueryId,
      resetSearchQueryId,
      destinationSelection,
      setDestinationSelection,
      updateDestination,
      updateDates,
      updateOccupations,
      clearDestination,
      resetSearch,
      isValidSearch
    }
  }, children);
};
export const useHotelsSearch = () => {
  const context = useContext(HotelsSearchContext);
  if (context === null) {
    throw new Error("useHotelsSearch must be used within a HotelsSearchProvider");
  }
  return context;
};